/**
 * API Client
 *
 * Client HTTP per comunicare con il backend PHC.
 * Gestisce automaticamente l'autenticazione tramite tokenStore.
 * Supporta sia MSAL (produzione) che dev mode.
 */

import { clearDevAuth } from "./devAuth";
import { getToken, refreshToken } from "./tokenStore";

// In production VITE_API_URL is empty (nginx proxies /api to backend)
// In dev mode, vite.config.js proxy handles /api requests
const API_BASE_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * Classe per errori API
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Ottiene gli headers per le richieste API
 * Usa tokenStore che viene aggiornato da AuthContext (MSAL o dev mode)
 */
function getHeaders() {
  const headers = {
    "Content-Type": "application/json",
  };

  // Ottieni token dal tokenStore (funziona sia con MSAL che dev mode)
  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

// Promise condivisa per il refresh token - evita refresh multipli paralleli
let refreshPromise = null;
// Flag per evitare redirect multipli a login
let isRedirecting = false;

/**
 * Esegue il refresh del token in modo sincronizzato
 * Se un refresh è già in corso, ritorna la stessa Promise
 */
async function doTokenRefresh() {
  // Se c'è già un refresh in corso, aspetta quello
  if (refreshPromise) {
    console.log("[API] Token refresh already in progress, waiting...");
    return refreshPromise;
  }

  console.log("[API] Starting token refresh...");
  refreshPromise = refreshToken()
    .then((token) => {
      console.log(
        "[API] Token refresh completed:",
        token ? "success" : "failed",
      );
      return token;
    })
    .catch((err) => {
      console.error("[API] Token refresh error:", err);
      return null;
    })
    .finally(() => {
      // Reset dopo un breve delay per evitare race condition
      setTimeout(() => {
        refreshPromise = null;
      }, 100);
    });

  return refreshPromise;
}

/**
 * Redirect a login (una sola volta)
 */
function redirectToLogin() {
  if (isRedirecting) return;
  if (window.location.pathname.includes("/login")) return;

  isRedirecting = true;
  console.log("[API] Redirecting to login...");
  clearDevAuth();
  window.location.href = "/login";
}

/**
 * Gestisce la risposta HTTP
 * @param {Response} response - Risposta fetch
 * @param {Function} retryFn - Funzione per retry (opzionale, per refresh token)
 * @param {boolean} isRetry - Se questa è già una richiesta di retry
 */
async function handleResponse(response, retryFn = null, isRetry = false) {
  const contentType = response.headers.get("content-type");
  const isJson = contentType?.includes("application/json");

  if (!response.ok) {
    const errorData = isJson
      ? await response.json()
      : { message: response.statusText };

    // Se 401 e non è già un retry, prova a refreshare il token
    if (response.status === 401 && retryFn && !isRetry) {
      const newToken = await doTokenRefresh();
      if (newToken) {
        console.log("[API] Retrying request with new token...");
        // Retry con flag isRetry=true per evitare loop
        return retryFn(true);
      }
      // Refresh fallito, redirect a login
      redirectToLogin();
    } else if (response.status === 401) {
      // 401 su retry o senza retryFn - redirect a login
      redirectToLogin();
    }

    throw new ApiError(
      errorData.message || `HTTP ${response.status}`,
      response.status,
      errorData,
    );
  }

  return isJson ? response.json() : response.text();
}

/**
 * Client API
 */
const api = {
  /**
   * GET request
   */
  async get(endpoint) {
    const makeRequest = async (isRetry = false) => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "GET",
        headers: getHeaders(),
      });
      return handleResponse(response, makeRequest, isRetry);
    };
    return makeRequest();
  },

  /**
   * POST request
   */
  async post(endpoint, data) {
    const makeRequest = async (isRetry = false) => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response, makeRequest, isRetry);
    };
    return makeRequest();
  },

  /**
   * PUT request
   */
  async put(endpoint, data) {
    const makeRequest = async (isRetry = false) => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response, makeRequest, isRetry);
    };
    return makeRequest();
  },

  /**
   * PATCH request
   */
  async patch(endpoint, data) {
    const makeRequest = async (isRetry = false) => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response, makeRequest, isRetry);
    };
    return makeRequest();
  },

  /**
   * DELETE request
   */
  async delete(endpoint) {
    const makeRequest = async (isRetry = false) => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      return handleResponse(response, makeRequest, isRetry);
    };
    return makeRequest();
  },
};

// Endpoints specifici
export const rsaApi = {
  list: () => api.get("/api/rsa"),
  get: (id) => api.get(`/api/rsa/${id}`),
  create: (data) => api.post("/api/rsa", data),
  update: (id, data) => api.put(`/api/rsa/${id}`, data),
  delete: (id) => api.delete(`/api/rsa/${id}`),
};

export const fornitoriApi = {
  list: () => api.get("/api/fornitori"),
  get: (id) => api.get(`/api/fornitori/${id}`),
  create: (data) => api.post("/api/fornitori", data),
  update: (id, data) => api.put(`/api/fornitori/${id}`, data),
  delete: (id) => api.delete(`/api/fornitori/${id}`),
};

export const contrattiApi = {
  list: () => api.get("/api/contratti"),
  get: (id) => api.get(`/api/contratti/${id}`),
  create: (data) => api.post("/api/contratti", data),
  update: (id, data) => api.put(`/api/contratti/${id}`, data),
  delete: (id) => api.delete(`/api/contratti/${id}`),
};

export const scadenzeApi = {
  list: () => api.get("/api/scadenze"),
  get: (id) => api.get(`/api/scadenze/${id}`),
  create: (data) => api.post("/api/scadenze", data),
  update: (id, data) => api.put(`/api/scadenze/${id}`, data),
  complete: (id) => api.patch(`/api/scadenze/${id}/complete`, {}),
};

export const interventiApi = {
  list: () => api.get("/api/interventi"),
  get: (id) => api.get(`/api/interventi/${id}`),
  create: (data) => api.post("/api/interventi", data),
  update: (id, data) => api.put(`/api/interventi/${id}`, data),
};

export const documentiApi = {
  list: () => api.get("/api/documenti"),
  get: (id) => api.get(`/api/documenti/${id}`),
};

export const authApi = {
  me: () => api.get("/api/auth/me"),
  updateProfile: (data) => api.put("/api/auth/me", data),
  config: () => api.get("/api/auth/config"),
};

export const notificheApi = {
  // Templates
  getTemplates: (params) =>
    api.get(
      `/api/notifiche/templates${params ? "?" + new URLSearchParams(params) : ""}`,
    ),
  getTemplate: (id) => api.get(`/api/notifiche/templates/${id}`),
  createTemplate: (data) => api.post("/api/notifiche/templates", data),
  updateTemplate: (id, data) => api.put(`/api/notifiche/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/api/notifiche/templates/${id}`),

  // Regole
  getRegole: () => api.get("/api/notifiche/regole"),
  getRegola: (id) => api.get(`/api/notifiche/regole/${id}`),
  createRegola: (data) => api.post("/api/notifiche/regole", data),
  updateRegola: (id, data) => api.put(`/api/notifiche/regole/${id}`, data),
  deleteRegola: (id) => api.delete(`/api/notifiche/regole/${id}`),

  // Invio
  send: (data) => api.post("/api/notifiche/invia", data),
  preview: (data) => api.post("/api/notifiche/preview", data),
  risolviDestinatari: (data) =>
    api.post("/api/notifiche/destinatari/risolvi", data),

  // Storico
  getStorico: (params) =>
    api.get(
      `/api/notifiche/storico${params ? "?" + new URLSearchParams(params) : ""}`,
    ),
  getLog: (id) => api.get(`/api/notifiche/storico/${id}`),

  // Utils
  getTags: () => api.get("/api/notifiche/tags"),
  getStats: () => api.get("/api/notifiche/stats"),
  getConfig: () => api.get("/api/notifiche/configurazione"),
};

export const azioniCorrettiveApi = {
  list: (params) =>
    api.get(
      `/api/azioni-correttive${params ? "?" + new URLSearchParams(params) : ""}`,
    ),
  get: (id) => api.get(`/api/azioni-correttive/${id}`),
  create: (data) => api.post("/api/azioni-correttive", data),
  update: (id, data) => api.put(`/api/azioni-correttive/${id}`, data),
  cambiaStato: (id, data) =>
    api.patch(`/api/azioni-correttive/${id}/stato`, data),
  chiudi: (id, data) => api.patch(`/api/azioni-correttive/${id}/chiudi`, data),
  stats: () => api.get("/api/azioni-correttive/stats"),
  utenti: () => api.get("/api/azioni-correttive/utenti"),
  inviaReminder: () => api.post("/api/azioni-correttive/invia-reminder", {}),
};

export const valutazioniApi = {
  list: (params) =>
    api.get(
      `/api/valutazioni-fornitori${params ? "?" + new URLSearchParams(params) : ""}`,
    ),
  dashboard: () => api.get("/api/valutazioni-fornitori/dashboard"),
  scadenze: () => api.get("/api/valutazioni-fornitori/scadenze"),
  get: (id) => api.get(`/api/valutazioni-fornitori/${id}`),
  create: (fornitoreId, data) =>
    api.post(`/api/fornitori/${fornitoreId}/valutazioni`, data),
  update: (id, data) => api.put(`/api/valutazioni-fornitori/${id}`, data),
  completa: (id) => api.put(`/api/valutazioni-fornitori/${id}/completa`),
  approva: (id) => api.put(`/api/valutazioni-fornitori/${id}/approva`),
  delete: (id) => api.delete(`/api/valutazioni-fornitori/${id}`),
  pdf: (id) =>
    fetch(`/api/valutazioni-fornitori/${id}/pdf`, {
      headers: { Authorization: `Bearer ${tokenStore.getToken()}` },
    }).then((r) => r.blob()),
  getByFornitore: (fornitoreId) =>
    api.get(`/api/fornitori/${fornitoreId}/valutazioni`),
};

export const criteriValutazioneApi = {
  list: (params) =>
    api.get(
      `/api/criteri-valutazione${params ? "?" + new URLSearchParams(params) : ""}`,
    ),
  get: (id) => api.get(`/api/criteri-valutazione/${id}`),
  create: (data) => api.post("/api/criteri-valutazione", data),
  update: (id, data) => api.put(`/api/criteri-valutazione/${id}`, data),
  delete: (id) => api.delete(`/api/criteri-valutazione/${id}`),
};

export default api;
